import classNames from 'classnames';
import { memo, useCallback, useState } from 'react';

import { Button } from '@air/primitive-button';

import { type AnalyticsEvent } from '../types';
import { formatJSON } from '../utils';
import { DataTableWithDetails } from './DataTableWithDetails';

type AnalyticsTabProps = {
  analyticsEvents: AnalyticsEvent[];
  analyticsLeftPanelWidth: number;
  setAnalyticsLeftPanelWidth: (width: number) => void;
  setAnalyticsEvents: (events: AnalyticsEvent[]) => void;
  onCopy: (text?: string) => void;
};

/**
 * Analytics tab showing captured analytics events with details panel.
 */
export const AnalyticsTab = memo(
  ({
    analyticsEvents,
    analyticsLeftPanelWidth,
    setAnalyticsLeftPanelWidth,
    setAnalyticsEvents,
    onCopy,
  }: AnalyticsTabProps) => {
    const [selectedAnalyticsEvent, setSelectedAnalyticsEvent] = useState<AnalyticsEvent | null>(null);

    const handleSetSelectedItem = useCallback((item: AnalyticsEvent | null) => {
      setSelectedAnalyticsEvent(item);
    }, []);
    const [expandedParams, setExpandedParams] = useState<Record<string, boolean>>({});
    const [expandedOptions, setExpandedOptions] = useState<Record<string, boolean>>({});

    return (
      <div className="flex h-full flex-col">
        <div className="shrink-0 space-y-3 border-b p-3 pt-0">
          <div className="flex items-center justify-end gap-2">
            <div className="flex items-center justify-end">
              <Button
                type="button"
                size="small"
                appearance="ghost"
                onClick={useCallback(() => setAnalyticsEvents([]), [setAnalyticsEvents])}
              >
                Clear
              </Button>
            </div>
          </div>
        </div>

        <DataTableWithDetails<AnalyticsEvent>
          leftPanelWidth={analyticsLeftPanelWidth}
          setLeftPanelWidth={setAnalyticsLeftPanelWidth}
          headerColumns={[
            { label: 'Event', span: 3 },
            { label: 'Type', align: 'right' },
          ]}
          data={analyticsEvents}
          selectedItem={selectedAnalyticsEvent}
          setSelectedItem={handleSetSelectedItem}
          renderRow={(event: AnalyticsEvent) => (
            <div className="grid grid-cols-4 gap-2">
              <span className="col-span-3 truncate font-medium">{event.event || event.type}</span>
              <span className="text-right font-medium font-mono uppercase tracking-wider">{event.type}</span>
            </div>
          )}
          renderDetails={(event: AnalyticsEvent) => (
            <>
              <div className="space-y-2 text-12">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-grey-9">Event ID:</span>
                  <span className="ml-4 text-grey-11">{event.id}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-grey-9">Type:</span>
                  <span className="ml-4 font-medium text-grey-11 uppercase tracking-wider">{event.type}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-grey-9">Timestamp:</span>
                  <span className="ml-4 text-grey-11">{event.timestamp.toLocaleTimeString()}</span>
                </div>
              </div>
              {!!event.event && (
                <div className="text-12">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-grey-9">Event:</span>
                    <span className="ml-4 max-w-xs truncate break-all font-mono text-grey-11">{event.event}</span>
                  </div>
                </div>
              )}
              {event.params && Object.keys(event.params).length > 0 && (
                <div className="text-12 text-grey-9">
                  <button
                    type="button"
                    onClick={() => {
                      const newExpanded = { ...expandedParams, [event.id]: !expandedParams[event.id] };
                      setExpandedParams(newExpanded);
                    }}
                    className="flex items-center gap-2 font-medium text-12 text-grey-9 hover:text-grey-11"
                  >
                    <span
                      className={classNames(
                        'transition-transform',
                        (expandedParams[event.id] ?? true) ? 'rotate-90' : '',
                      )}
                    >
                      ▶
                    </span>
                    <strong>Params:</strong>
                  </button>
                  {(expandedParams[event.id] ?? true) && (
                    <div className="mt-1 rounded border bg-grey-3 p-2 font-mono">{formatJSON(event.params)}</div>
                  )}
                </div>
              )}
              {event.options && Object.keys(event.options).length > 0 && (
                <div className="text-12 text-grey-9">
                  <button
                    type="button"
                    onClick={() => {
                      const newExpanded = { ...expandedOptions, [event.id]: !expandedOptions[event.id] };
                      setExpandedOptions(newExpanded);
                    }}
                    className="flex items-center gap-2 font-medium text-12 text-grey-9 hover:text-grey-11"
                  >
                    <span className={classNames('transition-transform', expandedOptions[event.id] ? 'rotate-90' : '')}>
                      ▶
                    </span>
                    <strong>Options:</strong>
                  </button>
                  {expandedOptions[event.id] && (
                    <div className="mt-1 rounded border bg-grey-3 p-2 font-mono">{formatJSON(event.options)}</div>
                  )}
                </div>
              )}
            </>
          )}
          emptyMessage="No analytics events captured yet. Try interacting with the app to see tracking events."
          detailsTitle="Event Details"
          copyButtonText="Copy JSON"
          onCopy={(event) => onCopy(JSON.stringify(event, null, 2))}
        />
      </div>
    );
  },
);

AnalyticsTab.displayName = 'AnalyticsTab';
